import { API_VERSION, CLIENT_VERSION, } from '@quicktl/shared';
class ApiError extends Error {
    status;
    body;
    constructor(status, body) {
        super(`API error ${status}: ${JSON.stringify(body)}`);
        this.status = status;
        this.body = body;
        this.name = 'ApiError';
    }
}
export { ApiError };
export class QuickTLClient {
    baseUrl;
    token;
    apiKey;
    constructor(config) {
        this.baseUrl = config.baseUrl.replace(/\/$/, '');
        this.token = config.token;
        this.apiKey = config.apiKey;
    }
    setToken(token) {
        this.token = token;
    }
    setApiKey(apiKey) {
        this.apiKey = apiKey;
    }
    headers() {
        const h = {
            'Content-Type': 'application/json',
            'X-Client-Version': CLIENT_VERSION,
        };
        if (this.apiKey)
            h['x-api-key'] = this.apiKey;
        else if (this.token)
            h['Authorization'] = `Bearer ${this.token}`;
        return h;
    }
    async request(method, path, body, query) {
        const params = query ? '?' + new URLSearchParams(query).toString() : '';
        const url = `${this.baseUrl}/${API_VERSION}${path}${params}`;
        const res = await fetch(url, {
            method,
            headers: this.headers(),
            body: body ? JSON.stringify(body) : undefined,
        });
        if (res.status === 204)
            return undefined;
        const data = await res.json().catch(() => null);
        if (!res.ok)
            throw new ApiError(res.status, data);
        return data;
    }
    paginationQuery(pagination) {
        const q = {};
        if (pagination?.page)
            q.page = String(pagination.page);
        if (pagination?.pageSize)
            q.pageSize = String(pagination.pageSize);
        return q;
    }
    async requestRaw(method, path, query) {
        const params = query ? '?' + new URLSearchParams(query).toString() : '';
        const url = `${this.baseUrl}/${API_VERSION}${path}${params}`;
        const headers = this.headers();
        delete headers['Content-Type'];
        const res = await fetch(url, { method, headers });
        if (!res.ok) {
            const data = await res.json().catch(() => null);
            throw new ApiError(res.status, data);
        }
        return res.text();
    }
    // ── Auth ──
    async register(data) {
        return this.request('POST', '/auth/register', data);
    }
    async login(data) {
        return this.request('POST', '/auth/login', data);
    }
    async requestPasswordReset(data) {
        return this.request('POST', '/auth/password-reset/request', data);
    }
    async validatePasswordResetToken(data) {
        return this.request('POST', '/auth/password-reset/validate', data);
    }
    async confirmPasswordReset(data) {
        return this.request('POST', '/auth/password-reset/confirm', data);
    }
    async verifyEmail(data) {
        return this.request('POST', '/auth/email/verify', data);
    }
    async validateEmailVerificationToken(data) {
        return this.request('POST', '/auth/email/verify/validate', data);
    }
    async resendVerificationEmail() {
        return this.request('POST', '/auth/email/verify/resend');
    }
    async getMe() {
        return this.request('GET', '/auth/me');
    }
    async createApiKey(data) {
        return this.request('POST', '/auth/api-keys', data);
    }
    async listApiKeys(pagination) {
        return this.request('GET', '/auth/api-keys', undefined, this.paginationQuery(pagination));
    }
    async deleteApiKey(id) {
        return this.request('DELETE', `/auth/api-keys/${id}`);
    }
    // ── Billing ──
    async getBillingStatus() {
        return this.request('GET', '/billing/status');
    }
    async changePlan(plan) {
        return this.request('POST', '/billing/change-plan', { plan });
    }
    async cancelSubscription() {
        return this.request('POST', '/billing/cancel');
    }
    async getPaddleConfig() {
        return this.request('GET', '/billing/paddle-config');
    }
    // ── AI Provider Keys ──
    async createAiKey(data) {
        return this.request('POST', '/account/ai-keys', data);
    }
    async listAiKeys(pagination) {
        return this.request('GET', '/account/ai-keys', undefined, this.paginationQuery(pagination));
    }
    async deleteAiKey(id) {
        return this.request('DELETE', `/account/ai-keys/${id}`);
    }
    async getAiKeyUsage(id, filters) {
        const q = {};
        if (filters?.from)
            q.from = filters.from;
        if (filters?.to)
            q.to = filters.to;
        return this.request('GET', `/account/ai-keys/${id}/usage`, undefined, q);
    }
    async getAiUsage(filters) {
        const q = {};
        if (filters?.from)
            q.from = filters.from;
        if (filters?.to)
            q.to = filters.to;
        return this.request('GET', `/account/ai-usage`, undefined, q);
    }
    // ── Glossaries ──
    async createGlossary(data) {
        return this.request('POST', '/account/glossaries', data);
    }
    async listGlossaries(pagination) {
        return this.request('GET', '/account/glossaries', undefined, this.paginationQuery(pagination));
    }
    async getGlossary(id) {
        return this.request('GET', `/account/glossaries/${id}`);
    }
    async updateGlossary(id, data) {
        return this.request('PATCH', `/account/glossaries/${id}`, data);
    }
    async deleteGlossary(id) {
        return this.request('DELETE', `/account/glossaries/${id}`);
    }
    async addGlossaryTerms(glossaryId, terms) {
        return this.request('POST', `/account/glossaries/${glossaryId}/terms`, terms);
    }
    async updateGlossaryTerm(glossaryId, termId, data) {
        return this.request('PATCH', `/account/glossaries/${glossaryId}/terms/${termId}`, data);
    }
    async deleteGlossaryTerm(glossaryId, termId) {
        return this.request('DELETE', `/account/glossaries/${glossaryId}/terms/${termId}`);
    }
    // ── Projects ──
    async createProject(data) {
        return this.request('POST', '/projects', data);
    }
    async listProjects(pagination) {
        return this.request('GET', '/projects', undefined, this.paginationQuery(pagination));
    }
    async getProject(id) {
        return this.request('GET', `/projects/${id}`);
    }
    async updateProject(id, data) {
        return this.request('PATCH', `/projects/${id}`, data);
    }
    async deleteProject(id) {
        return this.request('DELETE', `/projects/${id}`);
    }
    async attachGlossary(projectId, glossaryId) {
        return this.request('POST', `/projects/${projectId}/glossaries`, { glossaryId });
    }
    async detachGlossary(projectId, glossaryId) {
        return this.request('DELETE', `/projects/${projectId}/glossaries/${glossaryId}`);
    }
    async listProjectGlossaries(projectId, pagination) {
        return this.request('GET', `/projects/${projectId}/glossaries`, undefined, this.paginationQuery(pagination));
    }
    async addLocale(projectId, locale, translate) {
        return this.request('POST', `/projects/${projectId}/locales`, { locale, translate });
    }
    async removeLocale(projectId, locale) {
        return this.request('DELETE', `/projects/${projectId}/locales/${locale}`);
    }
    // ── Branches ──
    async createBranch(projectId, data) {
        return this.request('POST', `/projects/${projectId}/branches`, data);
    }
    async listBranches(projectId, pagination) {
        return this.request('GET', `/projects/${projectId}/branches`, undefined, this.paginationQuery(pagination));
    }
    async getBranch(projectId, branch) {
        return this.request('GET', `/projects/${projectId}/branches/${branch}`);
    }
    async compareBranches(projectId, branch, target) {
        return this.request('GET', `/projects/${projectId}/branches/${branch}/compare/${target}`);
    }
    async mergeBranch(projectId, branch, data) {
        return this.request('POST', `/projects/${projectId}/branches/${branch}/merge`, data ?? {});
    }
    async deleteBranch(projectId, branch) {
        return this.request('DELETE', `/projects/${projectId}/branches/${branch}`);
    }
    // ── Translations ──
    async getTranslations(projectId, branch, locale, pagination) {
        const path = locale
            ? `/projects/${projectId}/branches/${branch}/translations/${locale}`
            : `/projects/${projectId}/branches/${branch}/translations`;
        return this.request('GET', path, undefined, this.paginationQuery(pagination));
    }
    async upsertTranslations(projectId, branch, locale, entries, translateLocales) {
        const query = {};
        if (translateLocales?.length)
            query.translate = translateLocales.join(',');
        return this.request('PUT', `/projects/${projectId}/branches/${branch}/translations/${locale}`, entries, query);
    }
    async updateTranslation(projectId, branch, locale, key, data) {
        return this.request('PATCH', `/projects/${projectId}/branches/${branch}/translations/${locale}/${key}`, data);
    }
    async deleteTranslation(projectId, branch, locale, key) {
        return this.request('DELETE', `/projects/${projectId}/branches/${branch}/translations/${locale}/${key}`);
    }
    // ── Import / Export ──
    async importTranslations(projectId, branch, locale, file, filename, translateLocales) {
        const form = new FormData();
        form.append('file', file, filename);
        form.append('locale', locale);
        if (translateLocales?.length)
            form.append('translate', translateLocales.join(','));
        const url = `${this.baseUrl}/${API_VERSION}/projects/${projectId}/branches/${branch}/import`;
        const headers = { 'X-Client-Version': CLIENT_VERSION };
        if (this.apiKey)
            headers['x-api-key'] = this.apiKey;
        else if (this.token)
            headers['Authorization'] = `Bearer ${this.token}`;
        const res = await fetch(url, { method: 'POST', headers, body: form });
        const data = await res.json();
        if (!res.ok)
            throw new ApiError(res.status, data);
        return data;
    }
    async exportTranslations(projectId, branch, locale, format, style) {
        const query = { locale };
        if (format)
            query.format = format;
        if (style)
            query.style = style;
        return this.requestRaw('GET', `/projects/${projectId}/branches/${branch}/export`, query);
    }
    // ── AI Translation ──
    async translate(projectId, branch, data) {
        return this.request('POST', `/projects/${projectId}/branches/${branch}/translate`, data);
    }
    async getTranslationJob(projectId, jobId) {
        return this.request('GET', `/projects/${projectId}/jobs/${jobId}`);
    }
    async listTranslationJobs(projectId, pagination) {
        return this.request('GET', `/projects/${projectId}/jobs`, undefined, this.paginationQuery(pagination));
    }
    // ── Webhooks ──
    async createWebhook(projectId, data) {
        return this.request('POST', `/projects/${projectId}/webhooks`, data);
    }
    async listWebhooks(projectId, pagination) {
        return this.request('GET', `/projects/${projectId}/webhooks`, undefined, this.paginationQuery(pagination));
    }
    async updateWebhook(projectId, webhookId, data) {
        return this.request('PATCH', `/projects/${projectId}/webhooks/${webhookId}`, data);
    }
    async rotateWebhookSecret(projectId, webhookId) {
        return this.request('POST', `/projects/${projectId}/webhooks/${webhookId}/rotate-secret`);
    }
    async deleteWebhook(projectId, webhookId) {
        return this.request('DELETE', `/projects/${projectId}/webhooks/${webhookId}`);
    }
    async getWebhookDeliveries(projectId, webhookId, pagination) {
        return this.request('GET', `/projects/${projectId}/webhooks/${webhookId}/deliveries`, undefined, this.paginationQuery(pagination));
    }
    // ── Translation Memory ──
    async listTranslationMemory(projectId, filters, pagination) {
        const q = { ...this.paginationQuery(pagination) };
        if (filters?.locale)
            q.locale = filters.locale;
        if (filters?.q)
            q.q = filters.q;
        return this.request('GET', `/projects/${projectId}/translation-memory`, undefined, q);
    }
    async setTranslationMemory(projectId, data) {
        return this.request('POST', `/projects/${projectId}/translation-memory`, data);
    }
    async deleteTranslationMemory(projectId, tmId) {
        return this.request('DELETE', `/projects/${projectId}/translation-memory/${tmId}`);
    }
}
